import { useMemo } from "react";
import { useModels } from "@/hooks/use-models";
import { getSavedEffort, getSavedModel, saveEffort, saveModel } from "@/lib/composer-prefs";
import { groupModelsByProvider } from "@/lib/model-groups";
import { EffortSelector } from "@/routes/dashboard/components/assistant/effort-selector";
import {
  ModelSelectorContent,
  ModelSelectorEmpty,
  ModelSelectorGroup,
  ModelSelectorItem,
  ModelSelectorList,
  ModelSelectorModelContext,
  ModelSelectorRoot,
  ModelSelectorSearch,
  ModelSelectorTrigger,
  ModelSelectorValue,
} from "@/routes/dashboard/components/assistant/model-selector";

/**
 * The composer's model picker, grouped by provider, with the reasoning effort control next to it.
 * Both choices are remembered per browser (lib/composer-prefs.ts), so the new tab and the side
 * panel share them.
 */
export function ComposerModelPicker() {
  const models = useModels();

  const displayModels = useMemo(
    () =>
      models.map((m) => ({
        ...m,
        icon: m.icon ? <img src={m.icon} alt="" className="size-4" /> : undefined,
      })),
    [models],
  );

  const groups = useMemo(() => groupModelsByProvider(displayModels), [displayModels]);

  return (
    <ModelSelectorRoot
      models={displayModels}
      defaultValue={getSavedModel()}
      onValueChange={saveModel}
      defaultEffort={getSavedEffort()}
      onEffortChange={saveEffort}
    >
      <ModelSelectorModelContext />
      <div className="flex min-w-0 shrink items-center gap-0.5">
        <ModelSelectorTrigger
          variant="ghost"
          size="sm"
          className="text-muted-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground h-7 min-w-0 shrink rounded-full px-2.5"
        >
          <ModelSelectorValue showEffort={false} />
        </ModelSelectorTrigger>
        <ModelSelectorContent searchable collisionPadding={8}>
          <ModelSelectorSearch placeholder="Search models..." />
          <ModelSelectorList>
            <ModelSelectorEmpty />
            {groups.map(({ provider, models: group }) => (
              <ModelSelectorGroup
                key={provider.id}
                heading={
                  <span className="flex items-center gap-1.5">
                    {provider.logo && <img src={provider.logo} alt="" className="size-3.5" />}
                    {provider.name}
                  </span>
                }
              >
                {group.map((m) => (
                  <ModelSelectorItem key={m.id} model={m}>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{m.name}</span>
                      {m.description && (
                        <span
                          className="text-muted-foreground truncate text-xs"
                          title={m.description}
                        >
                          {m.description}
                        </span>
                      )}
                    </span>
                  </ModelSelectorItem>
                ))}
              </ModelSelectorGroup>
            ))}
          </ModelSelectorList>
        </ModelSelectorContent>
        <EffortSelector />
      </div>
    </ModelSelectorRoot>
  );
}
